import React, { useState, useLayoutEffect } from 'react';
import { Phone, Mail, Clock, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from 'pages/HomePage/components/Header';
import Footer from 'pages/HomePage/components/Footer';
import { apiAppCMS } from 'api/request';
import urlApp from 'api/urlApp';
import { ContactFormData } from 'types/contact';
import './LienHePage.scss';

const LienHePage: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<ContactFormData>({ name: '', phone: '', email: '', content: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showError, setShowError] = useState(false);

  useLayoutEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).setAttribute('data-visible', 'true');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '200px 0px 200px 0px' }
    );
    document.documentElement.classList.add('js-animations-ready');
    document.querySelectorAll('[data-reveal],[data-stagger]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setForm(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) return;
    setIsSubmitting(true);
    setShowError(false);
    try {
      await apiAppCMS.post(urlApp.contactRequest, form);
      setShowToast(true);
      setForm({ name: '', phone: '', email: '', content: '' });
      setTimeout(() => setShowToast(false), 4000);
    } catch {
      setShowError(true);
      setTimeout(() => setShowError(false), 4000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="lh-main">
        <div className="lh-container">

          {/* ── LEFT: Contact info */}
          <div className="lh-info">
            <h2 className="lh-info__title">{t('lienHe.info.title')}</h2>
            <p className="lh-info__sub">
              {t('lienHe.info.sub')}{' '}
              <span className="lh-info__sub-highlight">{t('lienHe.info.subHighlight')}</span>{' '}
              {t('lienHe.info.subSuffix')}
            </p>

            {/* Contact grid */}
            <div className="lh-info__grid">
              <div className="lh-info__item">
                <div className="lh-info__icon-box">
                  <Phone size={20} />
                </div>
                <div>
                  <h3 className="lh-info__item-label">{t('lienHe.info.hotline')}</h3>
                  <a href="tel:0374888419" className="lh-info__item-value">0374 888 419</a>
                </div>
              </div>

              <div className="lh-info__item">
                <div className="lh-info__icon-box">
                  <Mail size={20} />
                </div>
                <div>
                  <h3 className="lh-info__item-label">{t('lienHe.info.email')}</h3>
                  <a href="mailto:contact@mklexpress.com" className="lh-info__item-value">
                    contact@mklexpress.com
                  </a>
                </div>
              </div>

              <div className="lh-info__item">
                <div className="lh-info__icon-box">
                  <Clock size={20} />
                </div>
                <div>
                  <h3 className="lh-info__item-label">{t('lienHe.info.hours')}</h3>
                  <p className="lh-info__item-value">{t('lienHe.info.hoursWeekdays')}</p>
                  <p className="lh-info__item-value">{t('lienHe.info.hoursSaturday')}</p>
                </div>
              </div>
            </div>

            {/* Branch card */}
            <div className="lh-branch">
              <div className="lh-branch__tabs">
                <button className="lh-branch__tab lh-branch__tab--active">
                  {t('lienHe.branch.tab')}
                </button>
              </div>

              <div className="lh-branch__body">
                <div className="lh-branch__addr">
                  <MapPin size={22} className="lh-branch__addr-icon" />
                  <div>
                    <h3 className="lh-branch__addr-name">{t('lienHe.branch.name')}</h3>
                    <p className="lh-branch__addr-text">{t('lienHe.branch.address')}</p>
                  </div>
                </div>

                {/* Map */}
                <div className="lh-branch__map-wrap">
                  <iframe
                    title={t('lienHe.branch.mapTitle')}
                    width="100%"
                    height="100%"
                    frameBorder={0}
                    scrolling="no"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent('60 Trương Công Giai, Cầu Giấy, Hà Nội')}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Form */}
          <div className="lh-form-card">
            <form className="lh-form" onSubmit={handleSubmit} noValidate>
              <div className="lh-form__group">
                <label htmlFor="name" className="lh-form__label">{t('lienHe.form.name')}</label>
                <input
                  type="text" id="name"
                  className="lh-form__input"
                  value={form.name} onChange={handleChange} required
                />
              </div>

              <div className="lh-form__group">
                <label htmlFor="phone" className="lh-form__label">{t('lienHe.form.phone')}</label>
                <input
                  type="tel" id="phone"
                  className="lh-form__input"
                  value={form.phone} onChange={handleChange} required
                />
              </div>

              <div className="lh-form__group">
                <label htmlFor="email" className="lh-form__label">{t('lienHe.form.email')}</label>
                <input
                  type="email" id="email"
                  className="lh-form__input"
                  value={form.email} onChange={handleChange} required
                />
              </div>

              <div className="lh-form__group">
                <label htmlFor="content" className="lh-form__label">
                  {t('lienHe.form.content')}{' '}
                  <span className="lh-form__label-opt">{t('lienHe.form.optional')}</span>
                </label>
                <textarea
                  id="content" rows={4}
                  className="lh-form__textarea"
                  value={form.content} onChange={handleChange}
                />
              </div>

              <div className="lh-form__submit-wrap">
                <button
                  type="submit"
                  className="lh-form__btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('lienHe.form.submitting') : t('lienHe.form.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Toast thành công */}
      {showToast && (
        <div className="lh-toast">
          {t('lienHe.toast')}
        </div>
      )}

      {/* Toast lỗi */}
      {showError && (
        <div className="lh-toast lh-toast--error">
          Gửi thất bại. Vui lòng thử lại sau.
        </div>
      )}

      <Footer />
    </>
  );
};

export default LienHePage;
